import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'

export interface Transaction {
	id: string
	invoiceNo: number
	invType: number
	typeName: string
	invoiceDate: string
	shiftDate: string
	partner?: {
		code: string
		name?: string
		nameAr?: string
	}
	outlet: {
		from: string
		fromName?: string
		to?: string
		toName?: string
	}
	items: TransactionItem[]
	amounts: {
		grossAmount?: number
		discountAmount?: number
		vatAmount?: number
		netAmount?: number
		// Legacy field names from schema
		gross?: number
		discount?: number
		vat?: number
		net?: number
	}
	status: {
		posted: boolean
		postedAt?: string
		interfaced: boolean
		voided: boolean
		voidedAt?: string
		voidReason?: string
	}
	notes?: string
	reference?: string
	createdAt?: string
	updatedAt?: string
}

export interface TransactionItem {
	productCode: string
	productName: string
	productNameAr?: string
	qty: number
	unitCode: string
	unitPrice: number
	costPrice?: number
	discountPercent: number
	discountAmount: number
	vatPercent: number
	vatAmount: number
	lineAmount: number
	netAmount: number
	notes?: string
}

export interface CreateTransactionData {
	invType: number
	invoiceDate?: string
	shiftDate?: string
	partner?: {
		code: string
	}
	outlet: {
		from: string
		to?: string
	}
	items: Array<{
		productCode: string
		quantity: number
		unitCode?: string
		unitPrice: number
		unitCost?: number
		discountPercent?: number
		vatPercent?: number
		notes?: string
	}>
	discountPercent?: number
	vatPercent?: number
	notes?: string
	reference?: string
}

export interface PostTransactionData {
	reason?: string
}

export interface VoidTransactionData {
	reason: string
}

interface TransactionsResponse {
	data: Transaction[]
	pagination: {
		page: number
		limit: number
		total: number
		totalPages: number
	}
}

interface TransactionsParams {
	page?: number
	limit?: number
	type?: number
	outletCode?: string
	startDate?: string
	endDate?: string
	posted?: boolean
	voided?: boolean
	search?: string
}

export const useTransactions = (params: TransactionsParams = {}) => {
	const { page = 1, limit = 20, type, outletCode, startDate, endDate, posted, voided, search } = params

	return useQuery({
		queryKey: ['transactions', { page, limit, type, outletCode, startDate, endDate, posted, voided, search }],
		queryFn: () =>
			apiClient.get<TransactionsResponse>('/transactions', {
				page,
				limit,
				type,
				outletCode,
				startDate,
				endDate,
				posted,
				voided,
				search,
			}),
	})
}

export const useTransaction = (id: string) => {
	return useQuery({
		queryKey: ['transaction', id],
		queryFn: () => apiClient.get<Transaction>(`/transactions/${id}`),
		enabled: !!id,
	})
}

export const useCreateTransaction = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (data: CreateTransactionData) =>
			apiClient.post<Transaction>('/transactions', data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['transactions'] })
			queryClient.invalidateQueries({ queryKey: ['dashboard'] })
		},
	})
}

export const usePostTransaction = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data?: PostTransactionData }) =>
			apiClient.post<Transaction>(`/transactions/${id}/post`, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['transactions'] })
			queryClient.invalidateQueries({ queryKey: ['dashboard'] })
			queryClient.invalidateQueries({ queryKey: ['inventory'] })
		},
	})
}

export const useVoidTransaction = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: VoidTransactionData }) =>
			apiClient.post<Transaction>(`/transactions/${id}/void`, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['transactions'] })
			queryClient.invalidateQueries({ queryKey: ['dashboard'] })
			queryClient.invalidateQueries({ queryKey: ['inventory'] })
		},
	})
}
